/**
 * Agent capability checking utilities for the Knoc creation wizard.
 * These functions determine whether an agent has the required capabilities
 * for different knoc types.
 */

export type Agent = {
  ID: string;
  Name: string;
  LastSeen?: string;
  Token?: string;
  Capabilities?: Record<string, unknown> | null;
};

/**
 * Maps vendor names to their corresponding capability keys in the agent.
 * Used by the Application knoc type wizard.
 */
export const vendorToCapabilityMap: Record<string, string> = {
  fortinet: "fortios",
  fortimanager: "fortimanager",
  palo: "panos",
  firepower: "firepower",
  azure: "azure",
  sophosutm: "sophosutm",
  sophossfos: "sophossfos",
  aws: "aws",
  sonicwall: "sonicwall",
  cloudflarelist: "cloudflarelist",
};

/**
 * Check if an agent is capable for Application knoc type.
 *
 * @param agent - The agent to check
 * @param applicationType - The application type ('passive', 'passive+', or 'active')
 * @param vendor - The selected vendor (optional)
 * @returns boolean - Whether the agent can be used for this configuration
 */
export function isAgentCapableForApplication(
  agent: Agent | null | undefined,
  applicationType: string | undefined,
  vendor: string | undefined,
): boolean {
  // Passive application type doesn't require agent capabilities (server-side allowlist only)
  if (applicationType === "passive") {
    return false;
  }

  // For passive+ and active types, check if agent has the required vendor capability
  if (!vendor) {
    return Boolean(agent?.Capabilities && Object.entries(agent.Capabilities).length > 0);
  }

  const requiredCapability = vendorToCapabilityMap[vendor] || vendor;
  return Boolean(agent?.Capabilities?.[requiredCapability]);
}

/**
 * Check if an agent is capable for Linux (ipset) knoc type.
 * Requires ipset command to be available and sudo to be enabled.
 *
 * @param agent - The agent to check
 * @returns boolean - Whether the agent can be used for Linux knocs
 */
export function isAgentCapableForLinux(agent: Agent | null | undefined): boolean {
  const ipset = agent?.Capabilities?.ipset;
  if (ipset?.command_available && ipset?.sudo_enabled) {
    return true;
  }

  const pf = agent?.Capabilities?.pf;
  if (pf?.command_available && pf?.sudo_enabled) {
    return true;
  }

  return false;
}

/**
 * Check if an agent is capable for Proxy knoc type.
 *
 * @param agent - The agent to check
 * @param proxyType - The proxy type ('haproxy' or 'knocproxy')
 * @returns boolean - Whether the agent can be used for this proxy type
 */
export function isAgentCapableForProxy(
  agent: Agent | null | undefined,
  proxyType: "haproxy" | "knocproxy" = "haproxy",
): boolean {
  if (proxyType === "knocproxy") {
    return Boolean(agent?.Capabilities?.knocproxy);
  }
  return Boolean(agent?.Capabilities?.haproxy);
}

/**
 * Check if an agent is capable for Script knoc type.
 *
 * @param agent - The agent to check
 * @returns boolean - Whether the agent can be used for Script knocs
 */
export function isAgentCapableForScript(agent: Agent | null | undefined): boolean {
  return Boolean(agent?.Capabilities?.script);
}

/**
 * Check if an agent is capable for Windows Firewall knoc type.
 *
 * @param agent - The agent to check
 * @returns boolean - Whether the agent can be used for Windows knocs
 */
export function isAgentCapableForWindows(agent: Agent | null | undefined): boolean {
  return Boolean(agent?.Capabilities?.windows_firewall);
}

/**
 * Get the list of capability keys that an agent has.
 * Useful for displaying agent capabilities in the UI.
 *
 * @param agent - The agent to check
 * @returns string[] - List of capability keys
 */
export function getAgentCapabilityKeys(agent: Agent | null | undefined): string[] {
  if (!agent?.Capabilities) {
    return [];
  }
  return Object.keys(agent.Capabilities);
}

/**
 * Check if an agent has any capabilities at all.
 *
 * @param agent - The agent to check
 * @returns boolean - Whether the agent has any capabilities
 */
export function hasAnyCapabilities(agent: Agent | null | undefined): boolean {
  return Boolean(agent?.Capabilities && Object.keys(agent.Capabilities).length > 0);
}

/**
 * Warning object returned by getWarnings functions.
 */
export type AgentWarning = {
  message: string;
  linkUrl?: string;
  linkLabel?: string;
};

/**
 * Get warnings for Linux (ipset) knoc configuration.
 *
 * @param agent - The agent to check
 * @param ipv4SetName - The configured IPv4 set name (optional)
 * @param ipv6SetName - The configured IPv6 set name (optional)
 * @returns AgentWarning[] - Array of warnings
 */
export function getLinuxAgentWarnings(agent, ipv4SetName?: string, ipv6SetName?: string): AgentWarning[] {
  const warnings: AgentWarning[] = [];
  const ipsets = agent?.Capabilities?.ipset?.ipsets || [];
  const ipsetNames = ipsets.map((ipset) => ipset.name);

  if (ipv4SetName && !ipsetNames.includes(ipv4SetName)) {
    warnings.push({
      message: `The IPv4 set: ${ipv4SetName} has not been discovered on this agent. You will need to manually create it, check the documentation for more information.`,
      linkUrl: "https://docs.knocknoc.io/books/admin-guide/page/ipset-linux-netfilteriptables",
      linkLabel: "Ipset documentation (opens in new tab)",
    });
  }

  if (ipv6SetName && !ipsetNames.includes(ipv6SetName)) {
    warnings.push({
      message: `The IPv6 set: ${ipv6SetName} has not been discovered on this agent. You will need to manually create it, check the documentation for more information.`,
      linkUrl: "https://docs.knocknoc.io/books/admin-guide/page/ipset-linux-netfilteriptables",
      linkLabel: "Ipset documentation (opens in new tab)",
    });
  }

  return warnings;
}

/**
 * Get warnings for Windows firewall knoc configuration.
 *
 * @param agent - The agent to check
 * @param ruleName - The configured firewall rule name
 * @returns AgentWarning[] - Array of warnings
 */
export function getWindowsAgentWarnings(agent, ruleName?: string): AgentWarning[] {
  const warnings: AgentWarning[] = [];
  const availableRules = agent?.Capabilities?.windows_firewall?.available_rules || [];
  const ruleNames = availableRules.map((rule) => rule.name);

  if (ruleName && !ruleNames.includes(ruleName)) {
    warnings.push({
      message: `The windows firewall rule: ${ruleName} has not been discovered on this agent. You will need to manually create it, check the documentation for more information.`,
      linkUrl: "https://docs.knocknoc.io/books/admin-guide/page/windows-firewall",
      linkLabel: "Windows firewall documentation (opens in new tab)",
    });
  }

  return warnings;
}

/**
 * Get warnings for PF (Packet Filter) knoc configuration.
 *
 * @param agent - The agent to check
 * @param tableName - The configured PF table name
 * @returns AgentWarning[] - Array of warnings
 */
export function getPfAgentWarnings(agent, tableName?: string): AgentWarning[] {
  const warnings: AgentWarning[] = [];
  const tables = agent?.Capabilities?.pf?.tables || [];
  const tableNames = tables.map((table) => table.name);

  if (tableName && !tableNames.includes(tableName)) {
    // TODO(shh) - Fix this
    warnings.push({
      message: `The PF table: ${tableName} has not been discovered on this agent. You will need to manually create it, check the documentation for more information.`,
      linkUrl: "https://docs.knocknoc.io/books/admin-guide/page/pf-packet-filter",
      linkLabel: "PF documentation (opens in new tab)",
    });
  }

  return warnings;
}

/**
 * Get warnings for version capability issues.
 *
 * @param agent - The agent to check
 * @param isCapable - Function that checks if agent version is capable
 * @returns AgentWarning[] - Array of warnings
 */
export function getVersionWarnings(agent, isCapable: (agent) => boolean): AgentWarning[] {
  if (agent && !isCapable(agent)) {
    return [
      {
        message:
          "The Knoc type selected is not compatible with the agent's version. You will need to update the agent using your operating system's package manager to use this Knoc.",
      },
    ];
  }
  return [];
}
